import { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowScroll } from '@uidotdev/usehooks'
import _isArray from 'lodash/isArray'

import SearchInput from '../../../../components/SearchInput/SearchInput'
import Dropdown from '../../../../components/Dropdown/Dropdown'
import SelectYears from '../../../../pages/insights/components/SelectYears/SelectYears'
import useGroupModeOption from '../../../../hooks/ui/use_group_mode_option'
import FixedContentHeader from '../../../../components/FixedContentHeader/FixedContentHeader'
import useSortModeOption from '../../../../hooks/ui/use_sort_mode_option'
import { type DropdownOption } from '../../../../components/Dropdown/types'
import { type Emissions, type EmissionsFilters } from '../../../../types/data'
import { SortMode, GroupMode } from '../../../../types/ui'
import { CLASS_NAME } from './const'

import './style.scss'

type Props = {
  title: string
  filters: EmissionsFilters
  emissions: Emissions[] | null
  emissionsMinDate: Date | null
  emissionsMaxDate: Date | null
  setFilterValue: (key: string, value: string) => void
}

export default function TopBar({
  title,
  filters,
  emissions,
  setFilterValue,
  emissionsMinDate,
  emissionsMaxDate
}: Props) {
  const { query, groupMode, sortMode } = filters
  const { t } = useTranslation()
  const getUserHasTags = (emissions: Emissions[] | null): boolean => {
    if (!_isArray(emissions)) return false

    return emissions.some(
      ({ feature }) => feature?.properties?.tags?.length > 0
    )
  }

  const hasTags = getUserHasTags(emissions)

  const [{ y: windowScrollY }] = useWindowScroll()

  const groupModeOptions = useMemo(
    (): DropdownOption[] => [
      {
        label: t('analytics_sidebar_header.group_mode_none'),
        value: GroupMode.None
      },
      {
        label: t('analytics_sidebar_header.group_mode_country'),
        value: GroupMode.Country
      },
      {
        label: t('analytics_sidebar_header.group_mode_name'),
        value: GroupMode.Name
      },
      {
        label: t('analytics_sidebar_header.group_mode_land_cover'),
        value: GroupMode.LandCover
      },
      ...(hasTags
        ? [
            {
              label: t('analytics_sidebar_header.group_mode_tags'),
              value: GroupMode.Tags
            }
          ]
        : [])
    ],
    [t, hasTags]
  )

  const sortModeOptions = useMemo(
    (): DropdownOption[] => [
      {
        label: t('analytics_sidebar_header.sort_mode_none'),
        value: SortMode.None
      },
      {
        label: t('analytics_sidebar_header.sort_mode_area'),
        value: SortMode.Area
      },
      {
        label: t('analytics_sidebar_header.sort_mode_total_nee'),
        value: SortMode.TotalNEE
      },
      {
        label: t('analytics_sidebar_header.sort_mode_intensity'),
        value: SortMode.Intensity
      }
    ],
    [t]
  )

  const groupModeOption = useGroupModeOption(groupMode, groupModeOptions)
  const sortModeOption = useSortModeOption(sortMode, sortModeOptions)

  const onSetGroupingMode = useCallback(
    (option: any) => {
      const { value } = option

      setFilterValue('query', '')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setFilterValue('groupMode', value)
    },
    [setFilterValue]
  )

  const onSetSortMode = useCallback(
    (option: any) => {
      const { value } = option

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setFilterValue('sortMode', value)
    },
    [setFilterValue]
  )

  return (
    <FixedContentHeader
      isOverlapping={windowScrollY > 0}
      className={CLASS_NAME}
    >
      <div className={`${CLASS_NAME}-content`}>
        <h2>{title}</h2>
        <div className={`${CLASS_NAME}-controls`}>
          <SearchInput
            value={query}
            onChange={setFilterValue.bind(null, 'query')}
            placeholder={t('analytics_sidebar_header.search_placeholder')}
          />
          <Dropdown
            inline
            value={groupModeOption}
            onChange={onSetGroupingMode}
            options={groupModeOptions}
            placeholder={t(
              'analytics_sidebar_header.group_dropdown_placeholder'
            )}
          />
          <Dropdown
            inline
            value={sortModeOption}
            onChange={onSetSortMode}
            options={sortModeOptions}
            placeholder={t(
              'analytics_sidebar_header.sort_dropdown_placeholder'
            )}
          />
          <SelectYears
            filters={filters}
            emissions={emissions}
            maxDate={emissionsMaxDate}
            minDate={emissionsMinDate}
            setFilterValue={setFilterValue}
          />
        </div>
      </div>
    </FixedContentHeader>
  )
}
